import { useEffect, useMemo } from "react";

import PropTypes from "prop-types";

import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Grid, Button } from "@mui/material";

import { useAsync } from "../../../hooks/useAsync";

import LoaderWrapper from "../../wrapper/LoaderWrapper";

import { useForm } from "../../../hooks/useForm";

import useFilterSearchParams from "../../../hooks/useFilterSearchParams";
import useOrderService from "../../../services/orderService";

import UniversalFilterForm from "../../form/UniversalFilterForm";
import OrderChartToolBarForm from "./OrderChartToolBarForm";

import useDialog from "../../../hooks/useDialog";
import UserFilterDialog from "../../dialog/UserFilterDialog";

import {
  getSearchParamsObjectForFilterPrefix,
  getFilterSearchParamsKeyForField,
  isEmptyArray,
  isEmptyValue,
} from "../../../helpers/methods";

import FilterAltIcon from "@mui/icons-material/FilterAlt";

import {
  overflowButtonStyle,
  centerVericalAlignIconStyle,
} from "../../../helpers/styles";

const UserFilteringDialogForm = (props) => {
  return (
    <>
      <OrderChartToolBarForm
        {...props}
        onlyFormContent={false}
        isVerticalForm={true}
        filteringData={props.filteringData}
        showSelectTicketCategory={props.showSelectTicketCategory}
        showSelectTenant={props.showSelectTenant}
        showSelectOrderKt={props.showSelectOrderKt}
        showSelectOrderT={props.showSelectOrderT}
        showSelectSkill={props.showSelectSkill}
        showClearFiltersIcon={props.showClearFiltersIcon}
        showOpenCloseFilter={true}
      />
      <UniversalFilterForm
        {...props}
        filteringData={props.tableFilteringData}
        filtersConfig={props.tableFieldConfigs}
        style={{ marginTop: "4px" }}
      />
    </>
  );
};

export default function OrderChartToolBar(props) {
  const { t } = useTranslation();

  const [
    openUserFiltersDialog,
    onOpenUserFiltersDialog,
    onCloseUserFiltersDialog,
  ] = useDialog();

  const { getOrderChartFilteringData } = useOrderService();

  const filteringData = useAsync(getOrderChartFilteringData);

  const [searchParams, setSearchParams] = useSearchParams();
  const { getSearchParamsByFilterPrefix, clearSearchParamsByFilterPrefix } =
    useFilterSearchParams(searchParams);

  const {
    formValue,
    setFormValue,
    onChangeDateConvertedToDateTimeWithEndHourOfDate,
    onChangeDateConvertedToDateTimeWithZeroHourOfDate,
    onChangeDate,
    onChangeAutocompleteFieldWithObjectOptions,
  } = useForm(getSearchParamsByFilterPrefix(props.filterPrefix));

  useEffect(() => {
    setFormValue(getSearchParamsByFilterPrefix(props.filterPrefix));
  }, [searchParams]);

  const handleSearch = () => {
    setSearchParams(() => ({
      ...clearSearchParamsByFilterPrefix(props.filterPrefix),
      ...getSearchParamsObjectForFilterPrefix(formValue, props.filterPrefix),
      [getFilterSearchParamsKeyForField("page", props.filterPrefix)]: 1,
    }));
  };
  const handleCleanFilters = () => {
    setFormValue(() => ({}));
    setSearchParams(() => clearSearchParamsByFilterPrefix(props.filterPrefix));
  };

  const excludedFieldsForUserFilterDialog = useMemo(() => {
    let excludedFields = ["created"];

    if (!props.showSelectSkill) {
      excludedFields.push("skill");
    }

    if (!props.showSelectOrderT) {
      excludedFields.push("order_t");
    }

    if (!props.showSelectTenant) {
      excludedFields.push("tenant");
    }

    if (!props.showSelectTicketCategory) {
      excludedFields.push("ticket_category");
    }

    if (!props.showSelectOrderKt) {
      excludedFields.push("order_kt");
    }

    return excludedFields;
  }, [
    props.showSelectSkill,
    props.showSelectOrderT,
    props.showSelectTenant,
    props.showSelectTicketCategory,
    props.showSelectOrderKt,
  ]);

  const tableFiledsConfigForUserFilteringDialog = useMemo(
    () =>
      props.tableFieldConfigs.filter(
        (config) => !excludedFieldsForUserFilterDialog.includes(config.name)
      ),

    [props.tableFieldConfigs, excludedFieldsForUserFilterDialog]
  );

  const isLoading = formValue === undefined || !filteringData.value;

  const isFormValid = () => {
    let requiredFields = [];

    if (props.showSelectOrderKt) {
      requiredFields.push("order_kt");
    }

    return requiredFields.every((field) =>
      Array.isArray(formValue[field])
        ? !isEmptyArray(formValue[field])
        : !isEmptyValue(formValue[field])
    );
  };

  return (
    <LoaderWrapper showLoader={isLoading}>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        columnSpacing={3}
        rowSpacing={2}
        marginY={1}
      >
        {props.showMyFiltersButton && (
          <Grid item xs={12} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<FilterAltIcon sx={centerVericalAlignIconStyle} />}
              onClick={onOpenUserFiltersDialog}
              color={"primary"}
              sx={overflowButtonStyle}
            >
              {t("bar.tool_bar.my_filters")}
            </Button>
          </Grid>
        )}
        <OrderChartToolBarForm
          onlyFormContent={true}
          formValue={formValue}
          setFormValue={setFormValue}
          onChangeDateConvertedToDateTimeWithEndHourOfDate={
            onChangeDateConvertedToDateTimeWithEndHourOfDate
          }
          onChangeDateConvertedToDateTimeWithZeroHourOfDate={
            onChangeDateConvertedToDateTimeWithZeroHourOfDate
          }
          onChangeDate={onChangeDate}
          onChangeAutocompleteFieldWithObjectOptions={
            onChangeAutocompleteFieldWithObjectOptions
          }
          isVerticalForm={false}
          filteringData={filteringData}
          showSelectTicketCategory={props.showSelectTicketCategory}
          showSelectTenant={props.showSelectTenant}
          showSelectOrderKt={props.showSelectOrderKt}
          showSelectOrderT={props.showSelectOrderT}
          showSelectSkill={props.showSelectSkill}
          showClearFiltersIcon={props.showClearFiltersIcon}
        />

        <Grid
          container
          columnSpacing={2}
          item
          xs={12}
          md={props.showClearFiltersIcon ? 2.5 : 1.5}
          justifyContent={"flex-end"}
        >
          <Grid item xs={12} md={props.showClearFiltersIcon ? 5 : 12}>
            <Button
              variant="contained"
              color="success"
              size="small"
              fullWidth
              disabled={!isFormValid()}
              onClick={handleSearch}
            >
              {t("search")}
            </Button>
          </Grid>
          {props.showClearFiltersIcon && (
            <Grid item xs={12} md={7}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                fullWidth
                onClick={handleCleanFilters}
              >
                {t("clean_filters")}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
      {openUserFiltersDialog && (
        <UserFilterDialog
          open={openUserFiltersDialog}
          onClose={onCloseUserFiltersDialog}
          pageName={props.pageName}
          filterPrefix={props.filterPrefix}
          filterForm={
            <UserFilteringDialogForm
              {...props}
              filteringData={filteringData}
              tableFieldConfigs={tableFiledsConfigForUserFilteringDialog}
            />
          }
        />
      )}
    </LoaderWrapper>
  );
}

OrderChartToolBar.propTypes = {
  pageName: PropTypes.string,
  filterPrefix: PropTypes.string,
  showMyFiltersButton: PropTypes.bool,
  showSelectTicketCategory: PropTypes.bool,
  showSelectOrderKt: PropTypes.bool,
  showSelectTenant: PropTypes.bool,
  showSelectOrderT: PropTypes.bool,
  showSelectSkill: PropTypes.bool,
  showClearFiltersIcon: PropTypes.bool,
};

OrderChartToolBar.defaultProps = {
  showMyFiltersButton: true,
  showSelectTicketCategory: false,
  showSelectOrderKt: false,
  showSelectTenant: false,
  showClearFiltersIcon: true,
};
